import React, { useState, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Chip,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    AttachFile,
    Delete,
    Visibility,
    Download,
    CheckCircle,
    Warning
} from '@mui/icons-material';
import styles from './Decloration.module.css';
import { attachmentList, isFileInstance, normalizePickedFile } from './fileAttachment';

const ALLOWED_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

const validateOneFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
        return { valid: false, message: 'Неподдерживаемый тип файла. Разрешены: JPG, PNG, GIF, PDF, DOC, DOCX, XLS, XLSX' };
    }
    if (file.size > MAX_SIZE) {
        return { valid: false, message: 'Файл слишком большой. Максимальный размер: 10MB' };
    }
    return { valid: true };
};

const FileUploadWithPreview = ({ 
    field, 
    value, 
    onChange, 
    error, 
    required = false 
}) => {
    const fileInputRef = useRef(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState(null); // File or { url, name } for dialog
    const [uploading, setUploading] = useState(false);

    const isMultiple = !!field?.is_multiple;
    const valueList = isMultiple
        ? (Array.isArray(value) ? value : value != null ? [value] : [])
        : null;

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        const validFiles = [];
        for (let i = 0; i < files.length; i++) {
            const file = normalizePickedFile(files[i], i);
            const result = validateOneFile(file);
            if (!result.valid) {
                alert(result.message);
                break;
            }
            validFiles.push(file);
        }
        if (validFiles.length === 0) return;

        if (isMultiple) {
            const current = valueList || [];
            onChange([...current, ...validFiles]);
        } else {
            onChange(validFiles[0]);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (indexOrNull) => {
        if (isMultiple && (indexOrNull === 0 || indexOrNull > 0)) {
            const next = valueList.filter((_, i) => i !== indexOrNull);
            onChange(next.length ? next : null);
        } else {
            onChange(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handlePreview = (item) => {
        setPreviewItem(item ?? (isMultiple ? null : value));
        setPreviewOpen(true);
    };

    const getFileIcon = (fileType) => {
        if (!fileType) return '📎';
        if (fileType.startsWith('image/')) {
            return '🖼️';
        } else if (fileType === 'application/pdf') {
            return '📄';
        } else if (fileType.includes('word')) {
            return '📝';
        } else if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
            return '📊';
        }
        return '📎';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileValidationStatus = () => {
        if (isMultiple) {
            const list = attachmentList(valueList);
            if (required && list.length === 0) {
                return { valid: false, message: 'Необходимо прикрепить хотя бы один файл' };
            }
            if (list.length === 0) {
                return { valid: true, message: 'Файлы не выбраны' };
            }
            for (let i = 0; i < list.length; i++) {
                const item = list[i];
                if (isFileInstance(item)) {
                    const r = validateOneFile(item);
                    if (!r.valid) return { valid: false, message: `${item.name || 'Файл'}: ${r.message}` };
                }
            }
            return { valid: true, message: `Загружено файлов: ${list.length}` };
        }

        if (!value) {
            return { valid: false, message: required ? 'Файл обязателен' : 'Файл не выбран' };
        }
        if (value instanceof File) {
            const r = validateOneFile(value);
            if (!r.valid) return { valid: false, message: r.message };
            return { valid: true, message: 'Файл загружен корректно' };
        }
        // existing URL object
        return { valid: true, message: 'Файл загружен корректно' };
    };

    const validation = getFileValidationStatus();

    const fieldDisplayName = (field.label || field.name) === 'БСО'
        ? 'БСО (при наличии)'
        : (field.label || field.name);

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {fieldDisplayName}
                {required && <span style={{ color: 'red' }}> *</span>}
            </Typography>

            <Paper 
                elevation={1} 
                sx={{ 
                    p: 2, 
                    border: validation.valid ? '1px solid #e0e0e0' : '1px solid #ffcdd2',
                    backgroundColor: validation.valid ? 'inherit' : '#fff3f3'
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                    multiple={isMultiple}
                    style={{ display: 'none' }}
                />

                {isMultiple ? (
                    <>
                        {attachmentList(valueList).length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<AttachFile />}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ 
                                        mb: 2,
                                        color: '#EF2E25',
                                        borderColor: '#EF2E25',
                                        '&:hover': {
                                            borderColor: '#F36D52',
                                            backgroundColor: 'rgba(239, 46, 37, 0.04)'
                                        }
                                    }}
                                >
                                    Выбрать файлы
                                </Button>
                                <Typography variant="body2" color="text.secondary">
                                    Поддерживаемые форматы: JPG, PNG, GIF, PDF, DOC, DOCX, XLS, XLSX
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Максимальный размер файла: 10MB
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {attachmentList(valueList).map((item, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Typography variant="h5">
                                                {isFileInstance(item) ? getFileIcon(item.type || '') : '📎'}
                                            </Typography>
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {item.name || (item.url ? 'Прикрепленный файл' : 'Файл')}
                                                </Typography>
                                                {isFileInstance(item) && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatFileSize(item.size)} • {item.type}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <IconButton size="small" onClick={() => handlePreview(item)} title="Предпросмотр">
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleRemoveFile(valueList.indexOf(item))} title="Удалить" color="error">
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ))}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AttachFile />}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ alignSelf: 'flex-start', mt: 1, color: '#EF2E25', borderColor: '#EF2E25', '&:hover': { borderColor: '#F36D52', backgroundColor: 'rgba(239, 46, 37, 0.04)' } }}
                                >
                                    Добавить файл
                                </Button>
                            </Box>
                        )}
                    </>
                ) : !value ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Button
                            variant="outlined"
                            startIcon={<AttachFile />}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ 
                                mb: 2,
                                color: '#EF2E25',
                                borderColor: '#EF2E25',
                                '&:hover': {
                                    borderColor: '#F36D52',
                                    backgroundColor: 'rgba(239, 46, 37, 0.04)'
                                }
                            }}
                        >
                            Выбрать файл
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                            Поддерживаемые форматы: JPG, PNG, GIF, PDF, DOC, DOCX, XLS, XLSX
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Максимальный размер: 10MB
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h4">
                                {value instanceof File ? getFileIcon(value.type) : '📎'}
                            </Typography>
                            <Box>
                                <Typography variant="body1" fontWeight="medium">
                                    {value.name || 'Прикрепленный файл'}
                                </Typography>
                                {value instanceof File && (
                                    <Typography variant="body2" color="text.secondary">
                                        {formatFileSize(value.size)} • {value.type}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton size="small" onClick={() => handlePreview(value)} title="Предварительный просмотр">
                                <Visibility />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleRemoveFile()} title="Удалить файл" color="error">
                                <Delete />
                            </IconButton>
                        </Box>
                    </Box>
                )}

                {/* Validation Status */}
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {validation.valid ? (
                        <CheckCircle color="success" fontSize="small" />
                    ) : (
                        <Warning color="error" fontSize="small" />
                    )}
                    <Typography 
                        variant="caption" 
                        color={validation.valid ? 'success.main' : 'error.main'}
                    >
                        {validation.message}
                    </Typography>
                </Box>
            </Paper>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                </Alert>
            )}

            {/* File Preview Dialog */}
            <Dialog 
                open={previewOpen} 
                onClose={() => { setPreviewOpen(false); setPreviewItem(null); }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Предварительный просмотр файла
                </DialogTitle>
                <DialogContent>
                    {previewItem && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {previewItem.name || 'Файл'}
                            </Typography>
                            {previewItem instanceof File && (
                                <>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Размер: {formatFileSize(previewItem.size)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Тип: {previewItem.type}
                                    </Typography>
                                </>
                            )}
                            <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                                {previewItem instanceof File && previewItem.type.startsWith('image/') ? (
                                    <img 
                                        src={URL.createObjectURL(previewItem)} 
                                        alt={previewItem.name}
                                        style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                                    />
                                ) : previewItem instanceof File ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="h4" gutterBottom>
                                            {getFileIcon(previewItem.type)}
                                        </Typography>
                                        <Typography variant="body1" gutterBottom>
                                            {previewItem.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Предварительный просмотр недоступен для данного типа файла
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Download />}
                                            onClick={() => {
                                                const url = URL.createObjectURL(previewItem);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = previewItem.name;
                                                a.click();
                                                URL.revokeObjectURL(url);
                                            }}
                                            sx={{ mt: 2, color: '#EF2E25', borderColor: '#EF2E25', '&:hover': { borderColor: '#F36D52', backgroundColor: 'rgba(239, 46, 37, 0.04)' } }}
                                        >
                                            Скачать файл
                                        </Button>
                                    </Box>
                                ) : previewItem.url ? (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="h4" gutterBottom>📎</Typography>
                                        <Typography variant="body1" gutterBottom>{previewItem.name || 'Файл'}</Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Download />}
                                            onClick={() => window.open(previewItem.url, '_blank')}
                                            sx={{ mt: 2, color: '#EF2E25', borderColor: '#EF2E25', '&:hover': { borderColor: '#F36D52', backgroundColor: 'rgba(239, 46, 37, 0.04)' } }}
                                        >
                                            Открыть файл
                                        </Button>
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => { setPreviewOpen(false); setPreviewItem(null); }}
                        sx={{ color: '#EF2E25', '&:hover': { backgroundColor: 'rgba(239, 46, 37, 0.04)' } }}
                    >
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FileUploadWithPreview; 